import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Eye, Loader2, Mail, Search, Users, X } from 'lucide-react';
import {
  getPortalEmailComposeContext,
  getPortalEmailDirectory,
  sendPortalEmail,
} from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 100;
const MAX_COPY_RECIPIENTS = 25;
const inputClass = 'border-[rgba(6,43,26,0.16)] focus-visible:border-[#D4AF37] focus-visible:ring-[3px] focus-visible:ring-[#D4AF37]/25';

const AUDIENCE = {
  students: { singular: 'student', plural: 'students' },
  applications: { singular: 'applicant', plural: 'applicants' },
  agencies: { singular: 'agency', plural: 'agencies' },
  event_registrations: { singular: 'attendee', plural: 'attendees' },
  contact_inquiries: { singular: 'visitor', plural: 'visitors' },
  newsletter_subscribers: { singular: 'subscriber', plural: 'subscribers' },
  chat_conversations: { singular: 'visitor', plural: 'visitors' },
};
const FALLBACK_FIELDS = {
  students: ['Student name', 'First name', 'Last name', 'Email', 'Student number', 'Program'],
  applications: ['Student name', 'First name', 'Last name', 'Email', 'Application number', 'Program'],
  agencies: ['Agency company name', 'Agency contact name', 'First name', 'Last name', 'Email'],
  event_registrations: ['Attendee name', 'First name', 'Last name', 'Email', 'Event name', 'Event date', 'Registration number'],
  contact_inquiries: ['Recipient name', 'First name', 'Last name', 'Email', 'Inquiry reference', 'Inquiry subject'],
  newsletter_subscribers: ['Email', 'Unsubscribe link'],
  chat_conversations: ['Recipient name', 'First name', 'Email', 'Chat page'],
};
const fallbackFieldObjects = (audience) => (FALLBACK_FIELDS[audience] || [])
  .map((token) => ({ token, label: token }));

function errorText(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return 'The email could not be sent.';
}

function recipientEmail(record) {
  return String(record?.email || record?.mainContact?.email || '').trim().toLowerCase();
}

function recipientLabel(record, audience) {
  if (audience === 'agencies') {
    return record?.label || record?.company_name || record?.displayName || record?.preferredName || 'Agency';
  }
  return record?.label || record?.name || record?.full_name
    || `${record?.first_name || ''} ${record?.last_name || ''}`.trim()
    || recipientEmail(record) || 'Recipient';
}

export function EmailAddressPicker({ label, values, onChange, blockedEmails, automaticValues = [] }) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      getPortalEmailDirectory(query.trim())
        .then(({ data }) => {
          if (!cancelled) setOptions(Array.isArray(data?.items) ? data.items : []);
        })
        .catch((error) => {
          if (!cancelled) console.error(error);
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const existingEmails = new Set([...values, ...automaticValues].map((item) => item.email));
  const add = (entry) => {
    const email = String(entry?.email || '').trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      toast.error('Enter a valid email address.');
      return;
    }
    if (existingEmails.has(email) || blockedEmails.has(email)) {
      toast.error(`${email} is already included in this email.`);
      return;
    }
    if (values.length >= MAX_COPY_RECIPIENTS) {
      toast.error(`${label} supports up to ${MAX_COPY_RECIPIENTS} addresses.`);
      return;
    }
    onChange([...values, {
      email,
      label: entry.label || email,
      kind: entry.kind || 'email',
      detail: entry.detail || '',
    }]);
    setQuery('');
  };

  const addTyped = () => {
    const entries = query.split(/[,;]/).map((item) => item.trim()).filter(Boolean);
    if (!entries.length) return;
    let next = [...values];
    for (const email of entries) {
      const normalized = email.toLowerCase();
      if (!EMAIL_RE.test(normalized)) {
        toast.error(`${email} is not a valid email address.`);
        return;
      }
      if (blockedEmails.has(normalized) || next.some((item) => item.email === normalized)) continue;
      if (next.length >= MAX_COPY_RECIPIENTS) break;
      next.push({ email: normalized, label: normalized, kind: 'email', detail: '' });
    }
    onChange(next);
    setQuery('');
  };

  const visibleOptions = options
    .filter((item) => !existingEmails.has(item.email) && !blockedEmails.has(item.email))
    .slice(0, 8);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <Label>{label}</Label>
        {values.length > 0 && (
          <button
            type="button"
            aria-label={`Clear all ${label} recipients`}
            onClick={() => onChange([])}
            className="text-xs font-medium text-rose-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="rounded-lg border border-[#062B1A]/12 bg-white p-2">
        {automaticValues.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`${label.toLowerCase()}-automatic-recipients`}>
            {automaticValues.map((item) => (
              <span key={`automatic-${item.email}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs text-emerald-800">
                <span className="font-medium">{item.label || item.email}</span>
                <span className="text-emerald-700/65">{item.email}</span>
                <span className="rounded bg-white/70 px-1 text-[9px] font-semibold uppercase tracking-wide">Auto</span>
              </span>
            ))}
          </div>
        )}
        {values.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {values.map((item) => (
              <span key={item.email} className="inline-flex items-center gap-1 rounded-full border border-[#062B1A]/15 bg-[#F7F8FA] px-2 py-1 text-xs text-[#062B1A]">
                <span className="font-medium">{item.label}</span>
                {item.label !== item.email && <span className="text-[#062B1A]/50">{item.email}</span>}
                <button
                  type="button"
                  aria-label={`Remove ${item.email} from ${label}`}
                  title={`Remove ${item.email}`}
                  onClick={() => onChange(values.filter((value) => value.email !== item.email))}
                  className="rounded-full p-0.5 text-[#062B1A]/55 hover:bg-rose-100 hover:text-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#062B1A]/35" />
          <Input
            aria-label={`${label} recipients`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (visibleOptions.length === 1 && !EMAIL_RE.test(query.trim())) add(visibleOptions[0]);
                else addTyped();
              }
            }}
            placeholder="Search staff, students, agencies, or type an email"
            className={`pl-9 ${inputClass}`}
          />
        </div>
        {query && (
          <div className="mt-1 max-h-44 overflow-y-auto rounded-md border border-[#062B1A]/10 bg-white p-1 shadow-sm">
            {loading ? (
              <p className="px-2 py-2 text-xs text-[#062B1A]/45">Searching…</p>
            ) : visibleOptions.length > 0 ? visibleOptions.map((option) => (
              <button
                key={`${option.kind}:${option.id}:${option.email}`}
                type="button"
                onClick={() => add(option)}
                className="flex w-full items-center justify-between gap-3 rounded px-2 py-2 text-left hover:bg-[#F7F8FA]"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-[#062B1A]">{option.label}</span>
                  <span className="block truncate text-xs text-[#062B1A]/50">{option.email}</span>
                </span>
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-[#062B1A]/40">{option.kind}</span>
              </button>
            )) : (
              <button type="button" onClick={addTyped} className="w-full rounded px-2 py-2 text-left text-xs text-[#062B1A]/65 hover:bg-[#F7F8FA]">
                {EMAIL_RE.test(query.trim()) ? `Add ${query.trim()}` : 'Type a complete email address, then press Enter'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

const MERGE_TOKEN_RE = /\[([^[\]\r\n]{1,60})\]/g;

/**
 * Substitute [Friendly field] the way the server does — case-insensitively, on
 * collapsed whitespace — so the preview shows what will actually be sent.
 *
 * A token with no value behind it is deliberately left standing rather than
 * blanked: seeing `[Student number]` survive the preview is how somebody
 * notices they used a field this audience does not have.
 */
export function renderMergePreview(template, fields = {}) {
  const lookup = new Map(
    Object.entries(fields || {}).map(([key, value]) => [String(key).trim().toLowerCase(), value]),
  );
  return String(template || '').replace(MERGE_TOKEN_RE, (match, name) => {
    const key = String(name).trim().toLowerCase().replace(/\s+/g, ' ');
    const value = lookup.get(key);
    return value === undefined || value === null ? match : String(value);
  });
}

function attachmentStatusClass(status) {
  if (status === 'issued') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'draft') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-[#062B1A]/15 bg-[#F7F8FA] text-[#062B1A]/55';
}

function attachmentLabel(attachment) {
  return attachment?.file_name || attachment?.label || 'Document';
}

export default function PortalEmailComposer({
  audience,
  recipients = [],
  // Opening wording for a one-off reply — a subject already reading
  // "Re: …", say. Staff edit it like anything else; it only sets the
  // starting point so nobody retypes what the record already knows.
  defaults = null,
  buttonLabel = 'Email',
  buttonVariant = 'outline',
  buttonSize = 'sm',
  buttonClassName = '',
  iconOnly = false,
  disabled = false,
  offerPack = null,
  onSent,
}) {
  const config = AUDIENCE[audience] || AUDIENCE.students;
  const candidates = recipients.filter((item) => item?.id);
  const candidateIdsKey = candidates.map((item) => item.id).join('|');
  const [open, setOpen] = useState(false);
  const [recipientIds, setRecipientIds] = useState(() => new Set());
  const [sender, setSender] = useState('');
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [resolvedRecipients, setResolvedRecipients] = useState([]);
  // Addresses staff added to the To line. The record still supplies the first
  // one automatically; these are the people who should be addressed beside
  // them rather than merely copied in.
  const [additionalTo, setAdditionalTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('Dear [First name],\n\n\n\nBest regards,\nMetropolitan Community College');
  const [bodyHtml, setBodyHtml] = useState('');
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeField, setActiveField] = useState('body');
  const [presets, setPresets] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [presetId, setPresetId] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  const resolvedById = new Map(resolvedRecipients.map((item) => [item.id, item]));
  const displayCandidates = candidates.map((item) => ({ ...item, ...(resolvedById.get(item.id) || {}) }));
  const selected = displayCandidates.filter((item) => recipientIds.has(item.id));
  const deliverable = selected.filter((item) => EMAIL_RE.test(recipientEmail(item)));
  const missingEmailCount = selected.length - deliverable.length;
  const overLimit = selected.length > MAX_RECIPIENTS;
  const recipientWord = selected.length === 1 ? config.singular : config.plural;
  const automaticCc = Array.from(new Map(
    selected.flatMap((item) => safeList(item.automatic_cc)).map((item) => [item.email, item]),
  ).values());
  const primaryEmails = new Set(deliverable.map(recipientEmail));
  const automaticCcEmails = automaticCc.map((item) => item.email);
  const additionalToEmails = additionalTo.map((item) => item.email);
  // One address, one line. Being addressed and copied is not a stronger
  // send, it is the same person named twice in the header.
  const additionalToBlocked = new Set([
    ...primaryEmails,
    ...cc.map((item) => item.email),
    ...bcc.map((item) => item.email),
    ...automaticCcEmails,
  ]);
  const ccBlocked = new Set([
    ...primaryEmails, ...bcc.map((item) => item.email), ...automaticCcEmails,
    ...additionalToEmails,
  ]);
  const bccBlocked = new Set([
    ...primaryEmails, ...cc.map((item) => item.email), ...automaticCcEmails,
    ...additionalToEmails,
  ]);
  const attachments = safeList(offerPack?.attachments);
  const selectedAttachments = attachments.filter((item) => selectedDocumentIds.includes(item.id));
  const draftAttachments = selectedAttachments.filter((item) => item.status === 'draft');
  const availableFields = optionsLoaded
    ? mergeFields.filter((field) => field.audiences.includes(audience))
    : fallbackFieldObjects(audience);
  // The preview is one recipient's copy: everybody else receives the same
  // wording with their own values substituted, so rendering all of them
  // would be the same page repeated.
  const previewRecipient = deliverable[0] || null;
  const previewFields = previewRecipient?.fields || {};
  const previewSubject = renderMergePreview(subject, previewFields);
  const previewBody = renderMergePreview(body, previewFields);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingOptions(true);
    setOptionsLoaded(false);
    getPortalEmailComposeContext({
      audience,
      recipient_ids: candidateIdsKey.split('|').filter(Boolean),
    })
      .then(({ data }) => {
        if (cancelled) return;
        setResolvedRecipients(safeList(data.recipients));
        setPresets(safeList(data.presets));
        setMergeFields(data.merge_fields || []);
        const accounts = Array.isArray(data.sender_accounts) ? data.sender_accounts : [];
        setSenderAccounts(accounts);
        setSender((current) => accounts.some((account) => account.email === current)
          ? current : (offerPack?.sender && accounts.some((account) => account.email === offerPack.sender)
            ? offerPack.sender : (accounts[0]?.email || '')));
        setOptionsLoaded(true);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error(error);
          toast.error('Could not load email accounts and presets.');
        }
      })
      .finally(() => { if (!cancelled) setLoadingOptions(false); });
    return () => { cancelled = true; };
  }, [audience, candidateIdsKey, offerPack?.sender, open]);

  const reset = () => {
    setSubject(defaults?.subject || '');
    setBody(defaults?.body
      || 'Dear [First name],\n\n\n\nBest regards,\nMetropolitan Community College');
    setBodyHtml('');
    setSelectedDocumentIds([]);
    setConfirmed(false);
    setActiveField('body');
    setPresetId('');
    setPreviewing(false);
    setAdditionalTo([]);
    setCc([]);
    setBcc([]);
  };

  const openComposer = () => {
    setRecipientIds(new Set(candidates.map((item) => item.id)));
    setPreviewing(false);
    if (defaults && !offerPack) {
      if (defaults.subject) setSubject(defaults.subject);
      if (defaults.body) setBody(defaults.body);
    }
    if (offerPack) {
      setSubject(offerPack.subject || '');
      setBody(offerPack.body || '');
      setBodyHtml(offerPack.body_html || '');
      setSelectedDocumentIds(
        safeList(offerPack.attachments).filter((item) => item.default_selected).map((item) => item.id),
      );
      setBcc(offerPack.default_bcc ? [{
        email: offerPack.default_bcc,
        label: offerPack.default_bcc,
        kind: 'email',
        detail: '',
      }] : []);
      if (offerPack.sender) setSender(offerPack.sender);
      setResolvedRecipients(candidates.map((item) => ({
        ...item,
        automatic_cc: safeList(offerPack.automatic_cc),
      })));
    }
    setConfirmed(false);
    setOpen(true);
  };

  const toggleRecipient = (record) => {
    if (offerPack) return;
    setRecipientIds((current) => {
      const next = new Set(current);
      if (next.has(record.id)) next.delete(record.id);
      else {
        next.add(record.id);
        const email = recipientEmail(record);
        if (email) {
          setCc((items) => items.filter((item) => item.email !== email));
          setBcc((items) => items.filter((item) => item.email !== email));
        }
      }
      return next;
    });
    setConfirmed(false);
  };

  const applyPreset = (value) => {
    setPresetId(value);
    const preset = presets.find((item) => item.id === value);
    if (!preset) return;
    setSubject(preset.subject || '');
    setBody(preset.body || '');
    setConfirmed(false);
    setPreviewing(false);
  };

  const insertField = (name) => {
    const token = `[${name}]`;
    const isSubject = activeField === 'subject';
    const ref = isSubject ? subjectRef : bodyRef;
    const value = isSubject ? subject : body;
    const setValue = isSubject ? setSubject : setBody;
    const start = ref.current?.selectionStart ?? value.length;
    const end = ref.current?.selectionEnd ?? value.length;
    setValue(`${value.slice(0, start)}${token}${value.slice(end)}`);
    window.requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const send = async () => {
    if (!sender || !subject.trim() || !body.trim() || !confirmed || sending) return;
    if (offerPack && selectedAttachments.length === 0) {
      toast.error('Select at least one document to attach.');
      return;
    }
    try {
      setSending(true);
      const { data } = await sendPortalEmail({
        audience,
        recipient_ids: selected.map((item) => item.id),
        sender,
        additional_to: additionalTo.map((item) => item.email),
        cc: cc.map((item) => item.email),
        bcc: bcc.map((item) => item.email),
        subject: subject.trim(),
        body: body.trim(),
        body_html: bodyHtml,
        // Recorded, not acted on: the subject and body above are already the
        // final wording. This is how an audit answers "from which template?".
        template_id: presetId || '',
        attachment_application_id: offerPack?.applicationId || undefined,
        document_ids: offerPack ? selectedAttachments.map((item) => item.id) : [],
        confirm: true,
      });
      const extra = [data.failed ? `${data.failed} failed` : '', data.skipped ? `${data.skipped} skipped` : '']
        .filter(Boolean).join(', ');
      // A send that only finished a delivery an earlier attempt had already
      // made says so, rather than claiming to have sent something new.
      if (data.already_sent && data.already_sent >= data.sent) {
        toast.success('Already delivered earlier — the record has been brought up to date.');
      } else {
        toast.success(`${data.sent} email${data.sent === 1 ? '' : 's'} sent${extra ? ` (${extra})` : ''}.`);
      }
      setOpen(false);
      reset();
      onSent?.(data);
    } catch (error) {
      console.error(error);
      toast.error(errorText(error));
      if (offerPack) window.dispatchEvent(new Event('portal-documents-changed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        size={buttonSize}
        className={buttonClassName}
        aria-label={iconOnly ? buttonLabel : undefined}
        title={iconOnly ? buttonLabel : undefined}
        disabled={disabled || candidates.length === 0}
        onClick={(event) => { event.stopPropagation(); openComposer(); }}
      >
        <Mail className={`${iconOnly ? '' : 'mr-2'} h-4 w-4`} />
        {!iconOnly && buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={(next) => { if (!sending) setOpen(next); }}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" onClick={(event) => event.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Send email</DialogTitle>
            <DialogDescription>
              One composer is used across Students, Admissions, Agencies, Events, and document emails. Recipient and agency addresses are fetched from the current portal records.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>From account</Label>
                <Select value={sender} onValueChange={(value) => { setSender(value); setConfirmed(false); }} disabled={loadingOptions || senderAccounts.length === 0}>
                  <SelectTrigger aria-label="From account" className={inputClass}>
                    <SelectValue placeholder={loadingOptions ? 'Loading sending accounts…' : 'Select the sending account'} />
                  </SelectTrigger>
                  <SelectContent>
                    {senderAccounts.map((account) => (
                      <SelectItem key={account.email} value={account.email}>
                        {account.name} — {account.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/8 px-3 py-2 text-sm text-[#062B1A]/70">
                <p>
                  {deliverable.length} ready to receive email
                  {additionalTo.length > 0 && ` + ${additionalTo.length} added to To`}
                </p>
                {missingEmailCount > 0 && <p>{missingEmailCount} without a valid email will be skipped</p>}
                {overLimit && <p>Select no more than {MAX_RECIPIENTS} recipients at a time</p>}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label>To — choose who receives this email</Label>
                <span className="text-xs text-[#062B1A]/45">{selected.length} selected</span>
              </div>
              <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-[#062B1A]/12 p-2">
                {displayCandidates.map((record) => {
                  const email = recipientEmail(record);
                  return (
                    <label key={record.id} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-[#F7F8FA]">
                      <Checkbox checked={recipientIds.has(record.id)} onCheckedChange={() => toggleRecipient(record)} disabled={Boolean(offerPack)} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-[#062B1A]">{recipientLabel(record, audience)}</span>
                        <span className={`block truncate text-xs ${EMAIL_RE.test(email) ? 'text-[#062B1A]/50' : 'text-red-600'}`}>
                          {EMAIL_RE.test(email) ? email : 'No valid email — will be skipped'}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Addresses on the To line beside the record's own. Separate from
                CC because being addressed and being copied are different
                things to the person receiving it — and because a parent or a
                second contact is a recipient, not an observer. */}
            <EmailAddressPicker
              label="To — additional addresses"
              values={additionalTo}
              onChange={(value) => { setAdditionalTo(value); setConfirmed(false); }}
              blockedEmails={additionalToBlocked}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <EmailAddressPicker label="CC" values={cc} automaticValues={automaticCc} onChange={(value) => { setCc(value); setConfirmed(false); }} blockedEmails={ccBlocked} />
              <EmailAddressPicker label="BCC" values={bcc} onChange={(value) => { setBcc(value); setConfirmed(false); }} blockedEmails={bccBlocked} />
            </div>
            {(automaticCc.length > 0 || cc.length > 0 || bcc.length > 0 || additionalTo.length > 0) && (
              <p className="rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/8 px-3 py-2 text-xs text-[#7A5A05]">
                Linked agency emails are added to CC automatically for their own student or applicant. Additional To addresses are addressed alongside the recipient and can see each other. Manual CC and BCC contacts receive every personalized message in this send; BCC addresses remain hidden.
              </p>
            )}

            {!offerPack && <div className="space-y-2">
              <Label>Email preset</Label>
              <Select value={presetId} onValueChange={applyPreset} disabled={loadingOptions || presets.length === 0}>
                <SelectTrigger className={inputClass}>
                  <SelectValue placeholder={loadingOptions ? 'Loading presets…' : presets.length ? 'Choose a pre-written email' : 'No presets available for this panel'} />
                </SelectTrigger>
                <SelectContent>
                  {presets.map((preset) => <SelectItem key={preset.id} value={preset.id}>{preset.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#062B1A]/45">Choosing a preset fills the subject and message; you can still edit them before sending.</p>
            </div>}

            <div className="space-y-2">
              <Label htmlFor={`${audience}-email-subject`}>Subject</Label>
              <Input
                ref={subjectRef}
                id={`${audience}-email-subject`}
                value={subject}
                maxLength={255}
                onFocus={() => setActiveField('subject')}
                onChange={(event) => { setSubject(event.target.value); setConfirmed(false); }}
                placeholder="Email subject"
                className={inputClass}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor={`${audience}-email-body`}>Message</Label>
                <span className="text-xs text-[#062B1A]/45">Personalized automatically before sending</span>
              </div>
              {offerPack ? (
                <RichTextEditor
                  value={bodyHtml}
                  onChange={({ html, text }) => {
                    setBodyHtml(html);
                    setBody(text);
                    setConfirmed(false);
                  }}
                  disabled={sending}
                />
              ) : (
                <Textarea
                  ref={bodyRef}
                  id={`${audience}-email-body`}
                  value={body}
                  rows={12}
                  maxLength={20000}
                  onFocus={() => setActiveField('body')}
                  onChange={(event) => { setBody(event.target.value); setConfirmed(false); }}
                  className={inputClass}
                />
              )}
            </div>

            {!offerPack && <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#062B1A]/50">
                Insert an automatic field into the {activeField}
              </p>
              <div className="flex flex-wrap gap-2">
                {availableFields.map((field) => (
                  <Button key={`${field.token}-${field.id || ''}`} type="button" variant="outline" size="sm"
                    className="h-7 border-[#062B1A]/15 px-2 text-xs text-[#062B1A]"
                    onClick={() => insertField(field.token)}>
                    {field.label !== field.token ? `${field.label} ` : ''}[{field.token}]
                  </Button>
                ))}
              </div>
            </div>}

            {!offerPack && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label>Preview</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="toggle-preview"
                    disabled={!previewRecipient}
                    aria-expanded={previewing}
                    onClick={() => setPreviewing((current) => !current)}
                    className="h-7 border-[#062B1A]/15 px-2 text-xs text-[#062B1A]"
                  >
                    <Eye className="mr-1.5 h-3.5 w-3.5" />
                    {previewing ? 'Hide preview' : 'Preview email'}
                  </Button>
                </div>
                {previewing && previewRecipient && (
                  <div
                    data-testid="email-preview"
                    className="space-y-2 rounded-lg border border-[#062B1A]/12 bg-[#F7F8FA] p-3"
                  >
                    {/* One recipient's copy, merged the way the server will
                        merge it. Everyone else gets the same wording with
                        their own values, so showing all of them would be the
                        same page repeated. */}
                    <p className="text-xs text-[#062B1A]/50">
                      As {previewRecipient.label || previewRecipient.email} will receive it
                      {selected.length > 1 ? ` (1 of ${selected.length})` : ''}
                    </p>
                    <p className="text-sm font-semibold text-[#062B1A]" data-testid="preview-subject">
                      {previewSubject || <span className="text-[#062B1A]/40">No subject yet</span>}
                    </p>
                    <p
                      data-testid="preview-body"
                      className="whitespace-pre-wrap break-words text-sm leading-relaxed text-[#062B1A]/80"
                    >
                      {previewBody}
                    </p>
                  </div>
                )}
              </div>
            )}

            {offerPack && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label>Attachments</Label>
                  <span className="text-xs text-[#062B1A]/45">{selectedAttachments.length} selected</span>
                </div>
                <div className="space-y-2">
                  {attachments.map((attachment) => (
                    <label key={attachment.id} className="flex cursor-pointer gap-3 rounded-lg border border-[rgba(6,43,26,0.10)] bg-white px-3 py-3">
                      <Checkbox
                        checked={selectedDocumentIds.includes(attachment.id)}
                        onCheckedChange={(checked) => {
                          setSelectedDocumentIds((current) => (checked === true
                            ? [...new Set([...current, attachment.id])]
                            : current.filter((item) => item !== attachment.id)));
                          setConfirmed(false);
                        }}
                        className="mt-0.5"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-[#062B1A]">{attachment.label}</span>
                          <Badge variant="outline" className={attachmentStatusClass(attachment.status)}>
                            {attachment.status === 'issued' ? 'Confirmed (Issued)' : attachment.status_label}
                          </Badge>
                        </span>
                        <span className="mt-1 block truncate text-xs text-[#062B1A]/55">{attachmentLabel(attachment)}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {draftAttachments.length > 0 && (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Selected drafts will be confirmed as issued when this email is successfully sent: {draftAttachments.map(attachmentLabel).join(', ')}.
                  </p>
                )}
              </div>
            )}

            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-[#062B1A]/10 p-3 text-sm text-[#062B1A]/75">
              <Checkbox checked={confirmed} onCheckedChange={(value) => setConfirmed(value === true)} className="mt-0.5" />
              <span>
                I reviewed the From, To and any additional To addresses, automatic agency CC, manual CC/BCC, subject, message{offerPack ? ', and attachments' : ''} and confirm this should be sent to the selected {recipientWord}.
              </span>
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={sending}>Cancel</Button>
            <Button type="button" onClick={send}
              disabled={sending || loadingOptions || overLimit || deliverable.length === 0 || !sender || !subject.trim() || !body.trim() || !confirmed || (offerPack && selectedAttachments.length === 0)}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Users className="mr-2 h-4 w-4" />}
              {sending ? 'Sending…' : offerPack ? 'Confirm & send documents' : `Send ${deliverable.length} email${deliverable.length === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
